import { useState, useMemo, useCallback } from "react"
import { useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import type { SortDescriptor } from "react-aria-components"
import { TableBody as AriaTableBody } from "react-aria-components"
import { UserPlus01Icon, SearchMdIcon } from "@untitledui-icons/react/line"
import { Table, TableCard } from "@/components/application/table/table"
import { PaginationTable } from "@/components/application/pagination/pagination"
import { Input } from "@/components/ui/Input"
import { LoadingSpinner, EmptyState } from "@/components/common"
import { useLeadsList } from "@/hooks/api/useSignups"
import { useListPageSearch } from "@/hooks/useListPageSearch"
import type { Lead, LeadListFilters } from "@/types/signup"

// ============================================================================
// Status Badge
// ============================================================================

function timeAgo(date: Date): string {
  const now = new Date()
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000)
  if (seconds < 60) return "just now"
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  if (days < 30) return `${days}d ago`
  const months = Math.floor(days / 30)
  if (months < 12) return `${months}mo ago`
  return `${Math.floor(months / 12)}y ago`
}

const statusStyles: Record<Lead["status"], { bg: string; text: string }> = {
  DRAFT: {
    bg: "bg-gray-100 dark:bg-gray-800",
    text: "text-gray-700 dark:text-gray-300",
  },
  PENDING: {
    bg: "bg-amber-100 dark:bg-amber-900/40",
    text: "text-amber-700 dark:text-amber-300",
  },
  COMPLETED: {
    bg: "bg-green-100 dark:bg-green-900/40",
    text: "text-green-700 dark:text-green-300",
  },
  ABANDONED: {
    bg: "bg-red-100 dark:bg-red-900/40",
    text: "text-red-700 dark:text-red-300",
  },
}

function StatusBadge({ status }: { status: Lead["status"] }) {
  const style = statusStyles[status] ?? statusStyles.DRAFT
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${style.bg} ${style.text}`}
    >
      {status}
    </span>
  )
}

// ============================================================================
// SignupsPage
// ============================================================================

/**
 * Signups list page - admin/partner only
 */
export function SignupsPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()

  // Search and pagination with stable callbacks
  const {
    searchQuery,
    handleSearch,
    filters: urlFilters,
    page,
    perPage,
    handlePageChange,
    handlePageSizeChange,
  } = useListPageSearch<LeadListFilters>({
    useJsonFilter: false,
    paramKey: "q",
    debounceMs: 300,
  })

  // Build filters from URL params (debounced)
  const filters = useMemo((): LeadListFilters => {
    const result: LeadListFilters = {}
    if (urlFilters.q) {
      result.q = urlFilters.q
    }
    return result
  }, [urlFilters.q])

  // Sort state (default descending by created_at)
  const [sortDescriptor, setSortDescriptor] = useState<SortDescriptor>({
    column: "created_at",
    direction: "descending",
  })

  // Fetch leads
  const { data, isLoading, isFetching } = useLeadsList({
    filters,
    page,
    perPage,
    sort: {
      field: sortDescriptor.column as string,
      order: sortDescriptor.direction === "ascending" ? "ASC" : "DESC",
    },
  })

  const leads = data?.data || []
  const totalCount = data?.total || 0
  const totalPages = Math.ceil(totalCount / perPage)

  // Handle row click
  const handleRowClick = useCallback(
    (lead: Lead) => {
      navigate(`/signups/${lead.id}`)
    },
    [navigate]
  )

  // Handle sort change
  const handleSortChange = useCallback((descriptor: SortDescriptor) => {
    setSortDescriptor(descriptor)
  }, [])

  // Check if filters are active
  const hasActiveFilters = !!searchQuery

  // Show initial loading only when we have no data yet and no filters
  const showInitialLoading = isLoading && !data && !hasActiveFilters

  if (showInitialLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  // Simple empty state (no data and no filters)
  if (!data && !hasActiveFilters && leads.length === 0) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <EmptyState
          title="No signups yet"
          description="Signups will appear here once leads start the onboarding flow."
          icon={<UserPlus01Icon className="h-full w-full" />}
        />
      </div>
    )
  }

  return (
    <TableCard.Root size="sm" className="flex h-full flex-col">
      <TableCard.Header
        title="Signups"
        contentTrailing={
          <div className="flex items-center gap-3">
            {/* Search */}
            <Input
              type="text"
              value={searchQuery}
              onChange={handleSearch}
              placeholder="Search signups..."
              leftIcon={<SearchMdIcon className="h-5 w-5" />}
              className="w-40 sm:w-56"
            />
          </div>
        }
      />

      {/* Loading bar */}
      {isFetching && (
        <div className="h-1 w-full overflow-hidden bg-gray-100">
          <div className="h-full w-1/3 animate-pulse bg-brand-500" />
        </div>
      )}

      {/* Content area - either table or empty state */}
      {leads.length === 0 ? (
        <div className="flex flex-1 items-center justify-center p-6">
          <EmptyState
            title="No signups found"
            description="Try adjusting your search query."
          />
        </div>
      ) : (
        <div className="flex-1 overflow-auto">
          <Table
            aria-label="Signups"
            sortDescriptor={sortDescriptor}
            onSortChange={handleSortChange}
          >
            <Table.Header>
              <Table.Head id="status" label="Status" isRowHeader />
              <Table.Head id="business_name" label="Business Name" />
              <Table.Head id="email" label="Email" allowsSorting />
              <Table.Head id="phone_number" label="Phone" />
              <Table.Head id="contact_name" label="Contact" />
              <Table.Head id="created_at" label="Created" allowsSorting />
            </Table.Header>

            <AriaTableBody>
              {leads.map((lead) => (
                <Table.Row
                  key={lead.id}
                  id={lead.id}
                  className="cursor-pointer"
                  onAction={() => handleRowClick(lead)}
                >
                  {/* Status */}
                  <Table.Cell>
                    <StatusBadge status={lead.status} />
                  </Table.Cell>

                  {/* Business Name */}
                  <Table.Cell>
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {lead.data?.business_name || "\u2014"}
                    </span>
                  </Table.Cell>

                  {/* Email */}
                  <Table.Cell>
                    <span className="text-sm text-gray-900 dark:text-gray-100">
                      {lead.email || "\u2014"}
                    </span>
                  </Table.Cell>

                  {/* Phone */}
                  <Table.Cell>
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      {lead.phone_number || "\u2014"}
                    </span>
                  </Table.Cell>

                  {/* Contact Name */}
                  <Table.Cell>
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      {[lead.data?.contact_first_name, lead.data?.contact_last_name]
                        .filter(Boolean)
                        .join(" ") || "\u2014"}
                    </span>
                  </Table.Cell>

                  {/* Created At */}
                  <Table.Cell>
                    <span className="text-sm text-gray-900 dark:text-gray-100">{timeAgo(new Date(lead.created_at))}</span>
                    <span className="ml-2 text-sm text-gray-400 dark:text-gray-500">{new Date(lead.created_at).toLocaleString()}</span>
                  </Table.Cell>
                </Table.Row>
              ))}
            </AriaTableBody>
          </Table>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <PaginationTable
          page={page}
          total={totalPages}
          perPage={perPage}
          onPageChange={handlePageChange}
          onPerPageChange={handlePageSizeChange}
        />
      )}
    </TableCard.Root>
  )
}

export default SignupsPage
